import React, { useEffect } from 'react';
import { Outlet } from 'react-router';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

import { useClientId } from '../contexts/ClientContext';

export const SU_WIDGET_TEST_ID = 'seller-university-widget';

/**
 * Widget Component
 *
 * Purpose:
 * This component serves as the layout wrapper for embeddable Seller University widgets.
 * It's designed to provide Seller University functionality that can be embedded in
 * external applications via iframe or future script-based integration.
 *
 * Key Features:
 * - Minimal layout wrapper for embedding
 * - Provides consistent widget container styling across all embedded views
 * - Serves as the root component for all /su-widgets routes
 * - Uses the same responsive design patterns as the main app
 *
 * Usage:
 * - Iframe embedding: <iframe src="https://sellercentral-domain.com/su-widgets/learning-pathway/123" />
 * - Future script embedding: SellerUniversityWidget.render({...})
 *
 * Route Structure:
 * - /su-widgets/* - All widget routes use this component as their layout
 * - Child routes render through the <Outlet /> component
 *
 *
 * @returns JSX.Element - The widget layout wrapper with outlet for child routes
 */
const Widget = () => {
  const { viewMetric } = useMetrics();
  const clientId = useClientId();
  useEffect(() => {
    viewMetric({
      feature: FeatureName.WidgetRoot,
      metadata: { clientId },
    });
  }, [viewMetric, clientId]);
  return (
    <div data-testid={SU_WIDGET_TEST_ID}>
      <div className="m-auto max-w-screen-lg">
        <Outlet />
      </div>
    </div>
  );
};

export default Widget;
